<template>
  <TravelersFilterPopup
    ref="calendarPopupRef"
    :title="props.title"
    :static-title="props.staticTitle"
    :is-active="props.isActive"
    :is-scroll="props.isScroll"
    :is-disabled-reset-button="props.isDisabledResetButton"
    :is-full-height="props.isFullHeight"
    :is-footer-buttons-vertical="props.isFooterButtonsVertical"
    :is-modal-only="props.isModalOnly"
    :modal-size="props.modalSize"
    :modal-no-padding="props.modalNoPadding"
    :modal-class="props.modalClass"
    :total-count="filterStore.totalCount"
    :is-loading="filterStore.isLoading"
    @open="open"
    @close="close"
    @reset="reset"
    @apply="apply"
  >
    <template #trigger="{ open }">
      <slot
        name="trigger"
        :open="open"
      >
        <FilterPopupChip
          :label="props.title"
          :selected="props.isActive"
          :is-scroll="props.isScroll"
          :items-count="props.itemsCount"
          @open="open"
        />
      </slot>
    </template>

    <template #default>
      <slot />
    </template>

    <template #header>
      <slot name="header" />
    </template>

    <template #footer>
      <slot name="footer" />
    </template>
  </TravelersFilterPopup>
</template>

<script lang="ts" setup>
  import { TravelersFilterPopup } from '@experience/travelers-filter-popup-package';
  import { useTourFiltersStore } from '@/entities/filter/store/tour';
  import type {
    ITourFilterPopupWrapperProps,
    IFilterPopupWrapperEmits,
  } from '@/entities/filter/popup-wrapper/model/interface.ts';
  import { useHeaderZIndex } from '@/shared/composables/useHeaderZIndex.ts';
  import FilterPopupChip from '../FilterPopupChip.vue';
  import { commitDisplayCurrencyChange, resetDisplayCurrencyDraft } from '@/shared/composables/useDisplayedCurrency';
  import { clearPriceFilterState } from '@/entities/filter/lib/clearPriceFilterOnDisplayCurrencyChange';

  const calendarPopupRef = ref<{ open: (isForced: boolean) => void } | null>(null);

  const { decreaseZIndex, increaseZIndex } = useHeaderZIndex();

  const props = defineProps<ITourFilterPopupWrapperProps>();

  const emit = defineEmits<IFilterPopupWrapperEmits>();

  const filterStore = useTourFiltersStore();

  const open = (isForced: boolean) => {
    resetDisplayCurrencyDraft();
    decreaseZIndex();
    emit('open', isForced);
  };

  const close = (isForciblyOpened: boolean) => {
    resetDisplayCurrencyDraft();
    increaseZIndex();
    filterStore.applyPreviousParams();
    emit('close', isForciblyOpened);
  };

  const reset = (isForciblyOpened: boolean) => {
    filterStore.reset(props.filterKey);
    emit('reset', isForciblyOpened);
  };

  const apply = (isForciblyOpened: boolean) => {
    increaseZIndex();
    const draftPrice = [...filterStore.state.price] as Array<number | null>;
    const currencyChanged = commitDisplayCurrencyChange();

    if (draftPrice.some((value) => value !== null)) {
      filterStore.state.price = draftPrice;
    } else if (currencyChanged) {
      clearPriceFilterState(filterStore.state, filterStore.searchState);
    }

    filterStore.search();
    emit('apply', isForciblyOpened);
  };

  const exposedOpenModal = () => {
    calendarPopupRef.value?.open(true);
  };

  defineExpose({
    open: exposedOpenModal,
  });
</script>
